/**
 * The library's book list, for the help under a "not found" answer
 * (refusal-help.ts): is the book the reader named in it, and which chapters?
 *
 * Fetched only when a refusal names a book, so most sessions never load it, and
 * at most once per session: every refusal card shares one promise. The source
 * is the reader manifest the library already uses (~16 KB gzipped).
 * `cache: "no-cache"` for the reason use-corpus-data.ts gives: a newly
 * published book must show up without waiting out a heuristic cache.
 *
 * Fails soft to null and the card falls back to the general help. A failure is
 * not cached, so the next refusal tries again.
 */
import { useEffect, useState } from "react";
import { toLibraryIndex, type LibraryIndex } from "./refusal-help";

let pending: Promise<LibraryIndex | null> | null = null;

function loadLibraryIndex(): Promise<LibraryIndex | null> {
  pending ??= fetch("/corpus-data/manifest.json", { headers: { Accept: "application/json" }, cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((data: unknown) => toLibraryIndex(data))
    .catch(() => null)
    .then((index) => {
      if (index === null) pending = null;
      return index;
    });
  return pending;
}

export function useLibraryIndex(enabled: boolean): { settled: boolean; index: LibraryIndex | null } {
  const [state, setState] = useState<{ settled: boolean; index: LibraryIndex | null }>({ settled: false, index: null });
  useEffect(() => {
    if (!enabled) return;
    // The fetch may settle after unmount; suppress that stale state write.
    let alive = true;
    void loadLibraryIndex().then((index) => {
      if (alive) setState({ settled: true, index });
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return state;
}
